import { createContext, useContext, useState, useCallback, useMemo, useEffect, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CurrencyConfig, DEFAULT_CURRENCY, getCurrencyConfig } from '@/config/currency';
import { FormatCurrencyOptions } from '@/lib/currency';
import { accountQueries } from '@/services/accountApi';
import { useAuth } from '@/contexts/AuthContext';

/** Options for the context-bound formatCurrency. Accepts a currency code string as override. */
interface ContextFormatCurrencyOptions extends Omit<FormatCurrencyOptions, 'currency'> {
  /** Override currency by code (e.g. 'USD', 'GBP') */
  currency?: string;
}

interface CurrencyContextValue {
  currency: CurrencyConfig;
  setCurrency: (code: string) => void;
  formatCurrency: (amount: number | null, options?: ContextFormatCurrencyOptions) => string;
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined);

interface CurrencyProviderProps {
  children: ReactNode;
  defaultCurrency?: string;
}

/**
 * Currency Provider Component
 *
 * Wrap your app with this provider to enable currency configuration.
 * When the user is authenticated, the currency is driven by accountBalance.currency from the balance endpoint.
 * Falls back to localStorage / default USD for unauthenticated users.
 */
export function CurrencyProvider({ children, defaultCurrency }: CurrencyProviderProps) {
  const { isAuthenticated } = useAuth();

  const [currency, setCurrencyState] = useState<CurrencyConfig>(() => {
    if (defaultCurrency) {
      return getCurrencyConfig(defaultCurrency);
    }
    // Try to load from localStorage (only in browser)
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem('phonesalad_currency');
      return stored ? getCurrencyConfig(stored) : DEFAULT_CURRENCY;
    }
    return DEFAULT_CURRENCY;
  });

  // When authenticated, sync currency from the account's balance currency
  const { data: balanceData } = useQuery({
    ...accountQueries.balance(),
    enabled: isAuthenticated,
  });

  useEffect(() => {
    const accountCurrency = balanceData?.accountBalance?.currency;
    if (accountCurrency) {
      setCurrencyState(getCurrencyConfig(accountCurrency));
    }
  }, [balanceData?.accountBalance?.currency]);

  const setCurrency = useCallback((code: string) => {
    const newCurrency = getCurrencyConfig(code);
    setCurrencyState(newCurrency);
    // Only use localStorage in browser (for unauthenticated fallback)
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      localStorage.setItem('phonesalad_currency', newCurrency.code);
    }
  }, []);

  const formatCurrency = useCallback((
    amount: number | null,
    options: ContextFormatCurrencyOptions = {}
  ): string => {
    const {
      decimalPlaces = currency.decimalPlaces,
      showSymbol = true,
      useIntl = false,
      currency: currencyOverride,
    } = options;

    // Determine which currency config to use
    const currencyConfig = currencyOverride ? getCurrencyConfig(currencyOverride) : currency;
    const currencyCode = currencyConfig.code;

    if (amount === null) {
      if (!showSymbol) return '---'
      return currencyConfig.symbolPosition === 'before'
        ? `${currencyConfig.symbol}---`
        : `---${currencyConfig.symbol}`
    }

    // Use Intl.NumberFormat for full localization
    if (useIntl) {
      return new Intl.NumberFormat(currencyConfig.locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      }).format(amount);
    }

    // Simple formatting with symbol
    const formatted = amount.toFixed(decimalPlaces);

    if (!showSymbol) {
      return formatted;
    }

    return currencyConfig.symbolPosition === 'before'
      ? `${currencyConfig.symbol}${formatted}`
      : `${formatted}${currencyConfig.symbol}`;
  }, [currency]);

  const value = useMemo(
    () => ({ currency, setCurrency, formatCurrency }),
    [currency, setCurrency, formatCurrency]
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

/**
 * Hook to use currency configuration
 *
 * @example
 * ```tsx
 * const { currency, formatCurrency, setCurrency } = useCurrency();
 *
 * // Format a price
 * const price = formatCurrency(9.99);
 *
 * // Change currency
 * setCurrency('EUR');
 * ```
 */
export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
